import { useCanPerformOrganizationAction } from "@hooks/permissions/organization/useCanPerformOrganizationAction";
import { OrganizationAction } from "@common/userOrganizationRoles";
import { isSingleEntityOnlyPlan } from "@common/constants/organization-billing";
import {
    validateClientBillingTransferPlan,
    validateDeleteClientPlan,
} from "@common/helpers/permissions/organizationRequestValidators";
import { FC, useCallback, useState } from "react";
import { CaptureClicks } from "@design-system/CaptureClicks/CaptureClicks";
import { useCancelBillingTransferRequestMutation } from "@api/billingTransfer.api";
import { useStateAwareNavigate } from "@hooks/useStateAwareNavigate";
import { useScope } from "@state/scopeContext.lib";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { PlainModal } from "@design-system/Modal/Modal";
import { Menu } from "@design-system/Menu/Menu.lib";
import { useClientActions } from "../clientActionsContext";
import { useTransferBillingFromClientRequest } from "../useTransferBillingFromClientRequest";
import { ClientDetailsRow } from "../views/clientViewConfig";
import { OrgWorkspaceWarningModal } from "./OrgWorkspaceWarningModal";
import { UpgradePlanToAddEntityModal } from "./UpgradePlanToAddEntityModal";
import { PlanManagedByClientModal } from "./PlanManagedByClientModal";
import { TransferBillingToClientMenuItems } from "./TransferBillingToClientMenuItems";
import { useCancelTransferRequest } from "./useCancelTransferRequest";

interface Props {
    client: ClientDetailsRow;
}

export const ClientActionsDropdown: FC<Props> = ({ client }) => {
    const navigate = useStateAwareNavigate();
    const { changeActiveWorkspace } = useScope();
    const { newAddClientFlow } = useTypedFlags();
    const [isOrgWarningVisible, setIsOrgWarningVisible] = useState(false);
    const [isUpgradePlanVisible, setIsUpgradePlanVisible] = useState(false);
    const [isPlanManagedByClientVisible, setIsPlanManagedByClientVisible] =
        useState(false);
    const canManageClient = useCanPerformOrganizationAction(
        OrganizationAction.MANAGE_CLIENT,
    );
    const canManageOrganizationBilling = useCanPerformOrganizationAction(
        OrganizationAction.MANAGE_ORGANIZATION_BILLING,
    );
    const {
        editClient,
        transferBillingToClient,
        requestBillingTransferFromClient,
        removeClient,
        deleteClient,
        transferBillingAndRemoveClient,
        addEntityToClient,
    } = useClientActions();

    const transferBillingFromClientRequest =
        useTransferBillingFromClientRequest(client);

    const cancelRequestMutation = useCancelBillingTransferRequestMutation();
    const cancelTransferRequest = useCancelTransferRequest();

    const handleCancelRequest = (requestId: string) =>
        cancelTransferRequest({
            cancel: () => cancelRequestMutation.mutateAsync(requestId),
            successMessage: "Transfer request canceled",
            errorMessage: "Failed to cancel billing ownership request",
        });

    const proceedAddEntity = useCallback(() => {
        if (newAddClientFlow) {
            addEntityToClient(client.id);
            return;
        }

        changeActiveWorkspace(client.id);
        navigate("/new-entity");
    }, [
        newAddClientFlow,
        addEntityToClient,
        client,
        changeActiveWorkspace,
        navigate,
    ]);

    const onAddEntity = useCallback(() => {
        if (client.isFirmWorkspace) {
            setIsOrgWarningVisible(true);
            return;
        }

        const isSingleEntityPlan = isSingleEntityOnlyPlan(client.plan);

        if (isSingleEntityPlan && client.isOrganizationBillingOwner) {
            setIsUpgradePlanVisible(true);
            return;
        }

        if (isSingleEntityPlan && !client.isOrganizationBillingOwner) {
            setIsPlanManagedByClientVisible(true);
            return;
        }

        proceedAddEntity();
    }, [client, proceedAddEntity]);

    const activeOrganizationOwnsBilling = client.isOrganizationBillingOwner;

    const clientOwnsBilling = !activeOrganizationOwnsBilling;

    const hasUsersToTransferBillingTo = client.hasUsersToTransferBillingTo;

    const planNotTransferableError = validateClientBillingTransferPlan(
        client.plan,
    );

    const renderTransferBillingToClient = () => {
        if (
            !canManageOrganizationBilling ||
            !activeOrganizationOwnsBilling ||
            client.isFirmWorkspace
        ) {
            return null;
        }

        return (
            <TransferBillingToClientMenuItems
                client={client}
                onTransfer={transferBillingToClient}
            />
        );
    };

    const renderClientRemoveOrDelete = () => {
        if (client.isFirmWorkspace) {
            return null;
        }

        if (clientOwnsBilling) {
            return (
                <Menu.Item onClick={() => removeClient(client)} destructive>
                    Remove client
                </Menu.Item>
            );
        } else if (hasUsersToTransferBillingTo) {
            return (
                <Menu.Item
                    onClick={() => transferBillingAndRemoveClient(client)}
                    disabled={!!planNotTransferableError}
                    disabledReason={planNotTransferableError}
                    destructive
                >
                    Remove client
                </Menu.Item>
            );
        }

        const planNotDeletableError = validateDeleteClientPlan(client.plan);

        return (
            <Menu.Item
                onClick={() => deleteClient(client)}
                disabled={!!planNotDeletableError}
                disabledReason={planNotDeletableError}
                destructive
            >
                Delete client
            </Menu.Item>
        );
    };

    const renderTransferRequestAction = () => {
        if (
            !clientOwnsBilling ||
            !canManageOrganizationBilling ||
            client.isFirmWorkspace
        ) {
            return null;
        }

        if (transferBillingFromClientRequest) {
            return (
                <Menu.Item
                    onClick={() =>
                        handleCancelRequest(transferBillingFromClientRequest.id)
                    }
                    disabled={cancelRequestMutation.isPending}
                >
                    Cancel billing ownership request
                </Menu.Item>
            );
        }

        return (
            <Menu.Item onClick={() => requestBillingTransferFromClient(client)}>
                Request billing from client
            </Menu.Item>
        );
    };

    const handleOrgWarningConfirm = useCallback(() => {
        setIsOrgWarningVisible(false);

        const isSingleEntityPlan = isSingleEntityOnlyPlan(client.plan);

        if (isSingleEntityPlan && client.isOrganizationBillingOwner) {
            setIsUpgradePlanVisible(true);
            return;
        }

        if (isSingleEntityPlan && !client.isOrganizationBillingOwner) {
            setIsPlanManagedByClientVisible(true);
            return;
        }

        proceedAddEntity();
    }, [client, proceedAddEntity]);

    return (
        <>
            <CaptureClicks>
                <Menu.More
                    variant="primary"
                    aria-label="Client actions"
                    align="start"
                    disabled={!canManageClient}
                >
                    <Menu.Item
                        onClick={() => {
                            editClient(client);
                        }}
                    >
                        Edit client
                    </Menu.Item>
                    <Menu.Item
                        onClick={() => {
                            onAddEntity();
                        }}
                    >
                        Add entity
                    </Menu.Item>
                    {renderTransferBillingToClient()}
                    {renderTransferRequestAction()}
                    {renderClientRemoveOrDelete()}
                </Menu.More>
            </CaptureClicks>

            <PlainModal
                backdrop="static"
                show={isOrgWarningVisible}
                onHide={() => setIsOrgWarningVisible(false)}
            >
                <OrgWorkspaceWarningModal
                    onClose={() => setIsOrgWarningVisible(false)}
                    onConfirm={handleOrgWarningConfirm}
                />
            </PlainModal>

            <PlainModal
                backdrop="static"
                show={isUpgradePlanVisible}
                onHide={() => setIsUpgradePlanVisible(false)}
            >
                <UpgradePlanToAddEntityModal
                    client={client}
                    onClose={() => setIsUpgradePlanVisible(false)}
                    onUpgradeSuccess={() => {
                        setIsUpgradePlanVisible(false);
                        proceedAddEntity();
                    }}
                />
            </PlainModal>

            <PlainModal
                backdrop="static"
                show={isPlanManagedByClientVisible}
                onHide={() => setIsPlanManagedByClientVisible(false)}
            >
                <PlanManagedByClientModal
                    onClose={() => setIsPlanManagedByClientVisible(false)}
                />
            </PlainModal>
        </>
    );
};
